import type { Action, ContentBlock } from '@beidou/agent-protocol';
import ReactMarkdown from 'react-markdown';
import { useEffect, useState, type ReactNode } from 'react';
import { DefaultGlyphIcon, DefaultRingIcon } from '../components/DefaultGlyphIcon';
import { CarouselScrollTrack } from '../components/CarouselScrollTrack';
import { carouselGridKind, isGridOnlyCarousel } from '../utils/cardTypes';
import { useLsjBncImage } from '../utils/lsjBncImage';

interface RendererProps {
  onAction: (action: Action) => void;
  variant?: 'chat' | 'result';
  markdownStreaming?: boolean;
}

function normalizeMarkdownText(text: string): string {
  return String(text || '')
    .replace(/\uFF0A/g, '*') // 全角星号 ＊ -> *
    .replace(/\\\*/g, '*'); // \* -> *
}

function formatPlayCount(n?: number): string {
  if (!n) return '';
  if (n >= 10000) return `${(n / 10000).toFixed(1)}万`;
  return String(n);
}

/** C 端兜底：过滤 URL / HTTP 状态等后台细节 */
function looksLikeBackendError(text: string): boolean {
  const lower = text.toLowerCase();
  return (
    lower.includes('http://') ||
    lower.includes('https://') ||
    lower.includes('localhost') ||
    lower.includes('internal server error') ||
    lower.includes('for url') ||
    lower.includes('traceback') ||
    /status\/?\s*5\d\d/.test(lower) ||
    text.length > 100
  );
}

function VideoCardView({
  block,
  onAction,
  size = 'thumb',
}: {
  block: Extract<ContentBlock, { type: 'video_card' }>;
  onAction: (action: Action) => void;
  size?: 'hero' | 'thumb' | 'detail';
}) {
  const coverUrl = block.cover_url;
  const { resolvedSrc, status } = useLsjBncImage(coverUrl);
  const [coverReady, setCoverReady] = useState(!coverUrl);
  const [coverError, setCoverError] = useState(false);
  const isHero = size === 'hero';
  const isDetail = size === 'detail';
  const cardClass = [
    'video-card',
    isHero ? 'video-card--hero' : '',
    isDetail ? 'video-card--detail' : '',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    setCoverReady(!coverUrl);
    setCoverError(false);
  }, [coverUrl]);

  useEffect(() => {
    if (status === 'error') {
      setCoverReady(true);
      setCoverError(true);
    }
  }, [status]);

  const placeholderVisible = Boolean(coverUrl) && (!coverReady || coverError);
  const skeletonVisible = Boolean(coverUrl) && !coverReady && !coverError;

  return (
    <div
      className={cardClass}
      onClick={() => block.action && onAction(block.action)}
    >
      <div className={`video-cover${coverReady ? '' : ' video-cover--loading'}`}>
        {resolvedSrc && (
          <img
            className="video-cover-img"
            src={resolvedSrc}
            alt={block.title}
            loading="lazy"
            onLoad={() => setCoverReady(true)}
            onError={(e) => {
              setCoverReady(true);
              setCoverError(true);
              e.currentTarget.style.display = 'none';
            }}
          />
        )}
        {skeletonVisible && <span className="cover-skeleton" aria-hidden />}
        <span className={`video-cover-placeholder${placeholderVisible ? ' visible' : ''}`}>🎬</span>
        {block.duration && <span className="video-duration">{block.duration}</span>}
        {size === 'hero' && block.action && (
          <span className="video-hero-play" aria-hidden>
            ▶
          </span>
        )}
        {isDetail && block.action && (
          <span className="video-detail-play" aria-hidden>
            ▶
          </span>
        )}
      </div>
      <div className="video-title">{block.title}</div>
      <div className="video-meta">
        {block.play_count ? `${formatPlayCount(block.play_count)}播放` : ''}
        {block.score ? ` · ${block.score}分` : ''}
      </div>
    </div>
  );
}

function LsjCoverImgOrFallback({
  src,
  className,
  alt,
  fallback,
}: {
  src?: string;
  className: string;
  alt: string;
  fallback: ReactNode;
}) {
  const { resolvedSrc, status } = useLsjBncImage(src);
  if (!src) return <>{fallback}</>;
  if (status === 'error') return <>{fallback}</>;
  if (!resolvedSrc) return <span className="cover-skeleton" aria-hidden />;
  return <img className={className} src={resolvedSrc} alt={alt} loading="lazy" />;
}

function CoverFeedCard({
  title,
  coverUrl,
  fallback,
  meta,
  onClick,
  coverClassName = 'post-cover',
}: {
  title: string;
  coverUrl?: string;
  fallback: string;
  meta?: string;
  onClick: () => void;
  coverClassName?: string;
}) {
  return (
    <div className="post-card" onClick={onClick}>
      <div className={coverClassName}>
        <LsjCoverImgOrFallback
          src={coverUrl}
          className="post-cover-img"
          alt={title}
          fallback={fallback}
        />
      </div>
      <div className="post-caption">
        <div className="post-title">{title}</div>
        {meta ? <div className="post-meta">{meta}</div> : null}
      </div>
    </div>
  );
}

function CarouselItems({
  block,
  onAction,
  variant,
  layoutMode,
}: {
  block: Extract<ContentBlock, { type: 'carousel' }>;
  onAction: (action: Action) => void;
  variant: 'chat' | 'result';
  layoutMode: 'horizontal' | 'grid' | 'hero-list';
}) {
  if (layoutMode === 'hero-list') {
    const heroIndex = block.hero_index ?? 0;
    const hero = block.items[heroIndex];
    const rest = block.items.filter((_, i) => i !== heroIndex);
    return (
      <div className="carousel-section carousel-section--hero-list">
        {block.title && <div className="carousel-section-title">{block.title}</div>}
        {hero && (
          <div className="carousel-hero">
            <BlockRenderer block={hero} onAction={onAction} variant="chat" heroSize="hero" />
          </div>
        )}
        {rest.length > 0 && (
          <CarouselScrollTrack className="carousel-track carousel-track--thumbs">
            {rest.map((item, i) => (
              <BlockRenderer key={i} block={item} onAction={onAction} variant="chat" heroSize="thumb" />
            ))}
          </CarouselScrollTrack>
        )}
      </div>
    );
  }

  if (layoutMode === 'grid') {
    const gridKind = carouselGridKind(block.items);
    return (
      <div className={`carousel-section carousel-section--grid carousel-section--grid-${gridKind}`}>
        {block.title && <div className="carousel-section-title">{block.title}</div>}
        <div className="carousel-grid">
          {block.items.map((item, i) => (
            <BlockRenderer key={i} block={item} onAction={onAction} variant={variant} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="carousel-section">
      {block.title && <div className="carousel-section-title">{block.title}</div>}
      <CarouselScrollTrack className="carousel-track">
        {block.items.map((item, i) => (
          <BlockRenderer key={i} block={item} onAction={onAction} variant={variant} />
        ))}
      </CarouselScrollTrack>
    </div>
  );
}

function CarouselBlock({
  block,
  onAction,
  variant,
}: {
  block: Extract<ContentBlock, { type: 'carousel' }>;
  onAction: (action: Action) => void;
  variant: 'chat' | 'result';
}) {
  const gridOnly = isGridOnlyCarousel(block.items);
  const initialLayout = gridOnly
    ? 'grid'
    : block.layout === 'hero-list'
      ? 'hero-list'
      : block.layout === 'grid' || variant === 'result'
        ? 'grid'
        : 'horizontal';
  const [view, setView] = useState<'horizontal' | 'grid' | 'hero-list'>(initialLayout);
  const canToggle = !gridOnly && Boolean(block.view_toggle) && block.items.length >= 4;

  const layoutMode = canToggle ? view : initialLayout;

  return (
    <div className="carousel-block">
      {canToggle && (
        <div className="carousel-view-toggle" role="tablist" aria-label="视图切换">
          <button
            type="button"
            role="tab"
            aria-selected={layoutMode !== 'grid'}
            className={`carousel-view-btn${layoutMode !== 'grid' ? ' active' : ''}`}
            onClick={() => setView(block.layout === 'hero-list' ? 'hero-list' : 'horizontal')}
          >
            列表
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={layoutMode === 'grid'}
            className={`carousel-view-btn${layoutMode === 'grid' ? ' active' : ''}`}
            onClick={() => setView('grid')}
          >
            网格
          </button>
        </div>
      )}
      <CarouselItems block={block} onAction={onAction} variant={variant} layoutMode={layoutMode} />
    </div>
  );
}

export function BlockRenderer({
  block,
  onAction,
  variant = 'chat',
  heroSize,
  markdownStreaming,
}: { block: ContentBlock; heroSize?: 'hero' | 'thumb' | 'detail'; markdownStreaming?: boolean } & RendererProps): ReactNode {
  switch (block.type) {
    case 'markdown':
      const markdownText = normalizeMarkdownText(block.text);
      return (
        <div className="bot-text">
          <ReactMarkdown>{markdownText}</ReactMarkdown>
          {markdownStreaming && <span className="markdown-stream-cursor" aria-hidden />}
        </div>
      );
    case 'carousel':
      return <CarouselBlock block={block} onAction={onAction} variant={variant} />;
    case 'video_card':
      return (
        <VideoCardView
          block={block}
          onAction={onAction}
          size={heroSize ?? (variant === 'result' ? 'hero' : 'thumb')}
        />
      );
    case 'comic_card':
      return (
        <CoverFeedCard
          title={block.title}
          coverUrl={block.cover_url}
          fallback="📚"
          meta={[block.author, block.chapter_count ? `${block.chapter_count}话` : '']
            .filter(Boolean)
            .join(' · ')}
          onClick={() => block.action && onAction(block.action)}
        />
      );
    case 'novel_card':
      return (
        <CoverFeedCard
          title={block.title}
          coverUrl={block.cover_url}
          fallback="📖"
          meta={[block.author, block.word_count].filter(Boolean).join(' · ')}
          onClick={() => block.action && onAction(block.action)}
        />
      );
    case 'gallery_card':
      return (
        <CoverFeedCard
          title={block.title}
          coverUrl={block.cover_url}
          fallback="🖼"
          meta={[
            block.image_count ? `${block.image_count}张` : '',
            block.play_count ? `${formatPlayCount(block.play_count)}浏览` : '',
          ]
            .filter(Boolean)
            .join(' · ')}
          onClick={() => block.action && onAction(block.action)}
        />
      );
    case 'post_card': {
      const likeText = block.like_count ? `${formatPlayCount(block.like_count)}赞` : '';
      const playText = block.play_count ? `${formatPlayCount(block.play_count)}浏览` : '';
      return (
        <CoverFeedCard
          title={block.title}
          coverUrl={block.cover_url}
          fallback="📝"
          meta={[block.author, likeText || playText].filter(Boolean).join(' · ')}
          onClick={() => block.action && onAction(block.action)}
        />
      );
    }
    case 'microdrama_card':
      return (
        <CoverFeedCard
          title={block.title}
          coverUrl={block.cover_url}
          fallback="📱"
          coverClassName="post-cover microdrama-cover"
          meta={block.play_count ? `${formatPlayCount(block.play_count)}播放` : undefined}
          onClick={() => block.action && onAction(block.action)}
        />
      );
    case 'button':
      if (block.variant === 'primary') {
        return (
          <div className="bot-action-row">
            <button type="button" className="action-btn primary" onClick={() => onAction(block.action)}>
              {block.label}
            </button>
          </div>
        );
      }
      return (
        <div className="bot-action-row bot-action-row--chips">
          <button type="button" className="suggestion-chip" onClick={() => onAction(block.action)}>
            {block.label}
          </button>
        </div>
      );
    case 'follow_up':
      return null;
    case 'empty_state':
      return (
        <div className={`empty-state empty-state--${block.severity}`}>
          <div className="empty-state-icon" aria-hidden>
            {block.severity === 'warn' ? <DefaultGlyphIcon /> : <DefaultRingIcon />}
          </div>
          <div className="empty-state-title">{block.title}</div>
          {block.reason && !looksLikeBackendError(block.reason) && (
            <div className="empty-state-reason">{block.reason}</div>
          )}
          {block.reason && looksLikeBackendError(block.reason) && (
            <div className="empty-state-reason">服务繁忙，请稍后再试，或换个说法看看～</div>
          )}
          {block.suggestions.length > 0 && (
            <div className="empty-state-suggestions">
              {block.suggestions.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  className="suggestion-chip"
                  onClick={() => onAction({ type: 'send_message', payload: chip.message })}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}
        </div>
      );
    case 'image':
      return (
        <div className="bot-image-wrap">
          <img
            src={block.url}
            alt={block.alt || ''}
            className="bot-image"
            onClick={() => block.action && onAction(block.action)}
          />
        </div>
      );
    default:
      return <div className="bot-text">[未知组件: {(block as ContentBlock).type}]</div>;
  }
}
